'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import React, { ReactNode, useState, useMemo, useCallback, useEffect } from 'react';

import { FilterContext } from './FilterContext';

export const FilterContextProvider = ({ children }: { children: ReactNode }) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  const [animalType, setAnimalType] = useState<'all' | 'dog' | 'cat'>(
    (searchParams.get('animal-type') as 'all' | 'dog' | 'cat') ?? 'all'
  );
  const [pubType, setPubType] = useState<'all' | 'продажа' | 'дарение' | 'случка'>(
    (searchParams.get('publication-type') as 'all' | 'продажа' | 'дарение' | 'случка') ?? 'all'
  );
  const [selectedBreeds, setSelectedBreeds] = useState<string[]>(
    searchParams.get('breeds')?.split(',') ?? []
  );

  const [isOpenPopover, setIsOpenPopover] = useState(false);

  // If filter opened the /publications page should not react on filters change while 'Применить'
  // button been pressed.
  const openPopoverHandle = (isOpen: boolean) => {
    setIsOpenPopover(isOpen);
  };

  // Функция обновления URL
  const updateUrlParams = useCallback(() => {
    const newParams = new URLSearchParams();

    if (animalType !== 'all') newParams.set('animal-type', animalType);
    if (pubType !== 'all') newParams.set('publication-type', pubType);
    if (selectedBreeds.length) newParams.set('breeds', selectedBreeds.join(','));

    router.replace(`${pathname}?${newParams.toString()}`, { scroll: false });
  }, [animalType, pathname, pubType, router, selectedBreeds]);

  // Восстановление состояния из sessionStorage
  useEffect(() => {
    // Восстанавливаем состояние фильтров из sessionStorage
    const storedAnimalType = sessionStorage.getItem('animal-type');
    const storedPubType = sessionStorage.getItem('publication-type');
    const storedBreeds = sessionStorage.getItem('breeds');

    // Проверяем, что состояние нужно обновить только если оно отличается от текущего
    if (storedAnimalType && storedAnimalType !== animalType) {
      setAnimalType(storedAnimalType as 'all' | 'dog' | 'cat');
    }

    if (storedPubType && storedPubType !== pubType) {
      setPubType(storedPubType as 'all' | 'продажа' | 'дарение' | 'случка');
    }

    if (storedBreeds && storedBreeds !== selectedBreeds.join(',')) {
      setSelectedBreeds(storedBreeds.split(','));
    }

    // Вызываем обновление URL с новыми параметрами
    if (!isOpenPopover) {
      updateUrlParams();
    }
  }, [animalType, isOpenPopover, pubType, selectedBreeds, updateUrlParams]);

  const contextValue = useMemo(
    () => ({
      animalType,
      pubType,
      selectedBreeds,
      setAnimalType,
      setPubType,
      setSelectedBreeds,
      openPopoverHandle
    }),
    [animalType, pubType, selectedBreeds]
  );

  return <FilterContext.Provider value={contextValue}>{children}</FilterContext.Provider>;
};
